import React, { Component } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';

import { withClientConfig, ExperimentsConsumer } from '@spotahome/soyuz/client';
import { ToastContainer } from '@spotahome/ui-library';

import { getSearchQuery } from '@common/graphql/queries/search';
import { getAreaInfoQuery } from '@common/graphql/queries/areas';

import {
  InstanceManager,
  CustomDimensionsManager,
  constants
} from '@common/tracking';

import SessionExpiredModal from '@common/components/SessionExpiredModal';
import FavoriteClaim from '@common/components/FavoriteClaim';
import FavoriteClaimLoginModal from '@common/components/FavoriteClaimLoginModal';
import Cookies from '@common/components/Cookies';
import AppModal from '@common/components/AppModal';
import HeaderContainer from '@common/components/HeaderContainer';
import InformationToastWrapper from '@common/components/InformationToast/index';
import AppBanner from '@common/components/AppBanner';

import CurrencyFormatter from '@utils/CurrencyFormatter';

import HomeCardBannerConfig from '@common/config/homeCardBannerConfig';

import SahContainer from '../../LEGACY/javascripts/ui/sah-container';
import Search from '../../LEGACY/javascripts/search/components/Search';

import { MQ_TABLET_MIN_WIDTH } from '../../common/constants';

import { isSeoMode } from './utils/seo-mode';

import poiShape from './shapes/poi';
import cityShape from './shapes/city';

import { getInitialFilters, adaptFilters } from './utils/filters';
import { buildHeadersData } from './builders/headers';
import { buildAppData } from './builders/app';

import { Provider } from './IsUserLoggedContext';

const SCROLL_SETTINGS = {
  elementId: 'search-scroll'
};

const getMetadataUrl = (url, city) => {
  const startIndex = url.indexOf(`/${city}`);
  const endIndex = url.length;

  return url.substring(startIndex, endIndex);
};

const getMetadataLocale = locale => (locale === 'en' ? '' : locale);
const SHOULD_SHIFT_MARKERS = true;
const MARKERS_PER_PAGE = 60;
const ACTIVE_BANNERS = HomeCardBannerConfig.getNumberActiveBanners();

class SearchPage extends Component {
  static NAME = 'SearchPage';

  static contextTypes = {
    data: PropTypes.shape({
      SearchPage: PropTypes.shape({
        poi: PropTypes.shape(poiShape),
        city: PropTypes.shape(cityShape).isRequired,
        maxPriceInCity: PropTypes.number.isRequired,
        seoLinks: PropTypes.arrayOf(
          PropTypes.shape({
            anchorText: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
          })
        ),
        initialFiltersFromQuery: PropTypes.object.isRequired
      }).isRequired
    }).isRequired
  };
  static propTypes = {
    clientConfig: PropTypes.shape({
      featureToggle: PropTypes.shape({
        showHomecardBanner: PropTypes.bool
      })
    }).isRequired
  };

  static async requestAllData(req, _, { clientConfig, graphqlClient }) {
    const { params, originalUrl, locale } = req;
    const { city, area } = params;

    try {
      const searchAlgorithmNamespace = req.getNamespace(
        'SearchAlgorithmNamespace'
      );

      let areaInfo;
      if (area) {
        const areaResponse = await getAreaInfoQuery(graphqlClient)(city, area);
        areaInfo = areaResponse.data.areaInfo;
      }

      const initialFiltersFromQuery = getInitialFilters(originalUrl, areaInfo);
      const adaptedFilters = adaptFilters(
        initialFiltersFromQuery,
        searchAlgorithmNamespace.get('algorithm_name')
      );

      const searchResponse = await getSearchQuery(graphqlClient)(
        city,
        adaptedFilters,
        getMetadataLocale(locale),
        initialFiltersFromQuery['move-in'],
        initialFiltersFromQuery['move-out'],
        getMetadataUrl(originalUrl, city),
        clientConfig.featureToggle.showHomecardBanner
          ? MARKERS_PER_PAGE - ACTIVE_BANNERS
          : MARKERS_PER_PAGE
      );

      if (searchResponse.errors) {
        req.logger.error(searchResponse.errors);
      }

      const { isLogged, metadata, ...searchData } = searchResponse.data;
      const { paginator } = searchData.search;
      const serverTrackingInstance = InstanceManager.getServerSideInstance();
      const cdManager = CustomDimensionsManager(serverTrackingInstance);

      cdManager.setSearchQueryCustomDimensions(
        city,
        adaptedFilters,
        paginator.count
      );

      cdManager.setSearchCommonCustomDimensions({
        city,
        moveIn: initialFiltersFromQuery['move-in'],
        moveOut: initialFiltersFromQuery['move-out'],
        locale,
        isLogged,
        pageType: constants.TRACKING_PAGE_TYPES.SEARCH_PAGE
      });
      const dataLayerScript = serverTrackingInstance.flush();

      return {
        headers: buildHeadersData(
          city,
          clientConfig,
          metadata,
          initialFiltersFromQuery,
          paginator,
          req,
          dataLayerScript
        ),
        data: buildAppData(
          { isLogged, ...searchData, metadata },
          initialFiltersFromQuery,
          SHOULD_SHIFT_MARKERS
        )
      };
    } catch (error) {
      console.error(error);
    }

    return {
      headers: {},
      body: {}
    };
  }

  state = {
    ...this.context.data[SearchPage.NAME]
  };

  render() {
    const {
      city,
      allCities,
      poi,
      maxPriceInCity,
      initialFiltersFromQuery,
      initialMarkerList = [],
      initialHomecards = {},
      initialUnitPrices,
      isUserLogged,
      numberOfMarkers,
      areas,
      metadata
    } = this.state;

    const countryCode = city.country.countryIsoCode;
    const cities = city.allCities;
    const otherCitiesInCountry = city.otherCitiesInCountry;
    const currency = new CurrencyFormatter(city.currencyIsoCode);
    const IS_SEO_MODE = isSeoMode(initialFiltersFromQuery);

    return (
      <SahContainer>
        <AppBanner source="search" />
        <HeaderContainer />
        <ToastContainer />
        <InformationToastWrapper city={this.state.city.id} />
        <Provider
          value={{
            isUserLogged,
            allCities
          }}
        >
          <ExperimentsConsumer namespace="SearchAlgorithmNamespace">
            {searchAlgorithmNamespace => (
              <Search
                discounts={city.discounts}
                sessionPoi={poi}
                city={city}
                countryCode={countryCode}
                currency={currency}
                maxPriceInCity={maxPriceInCity}
                initialFiltersFromQuery={initialFiltersFromQuery}
                seoMode={IS_SEO_MODE}
                seoContent={metadata.content}
                allCities={cities}
                otherCitiesInCountry={otherCitiesInCountry}
                initialMarkerList={initialMarkerList}
                initialHomecards={initialHomecards}
                initialUnitPrices={initialUnitPrices}
                numberOfMarkers={numberOfMarkers}
                sortingAlgorithm={searchAlgorithmNamespace.get(
                  'algorithm_name'
                )}
                itemsPerPage={
                  this.props.clientConfig.featureToggle.showHomecardBanner
                    ? MARKERS_PER_PAGE - ACTIVE_BANNERS
                    : MARKERS_PER_PAGE
                }
                areas={areas}
              />
            )}
          </ExperimentsConsumer>
        </Provider>
        <SessionExpiredModal />
        <ExperimentsConsumer namespace="PromoteSignupInFavoritesNamespace">
          {ns =>
            ns.get('behaviour') === 'original' ? (
              <FavoriteClaim />
            ) : (
              <FavoriteClaimLoginModal />
            )
          }
        </ExperimentsConsumer>
        <MediaQuery minWidth={MQ_TABLET_MIN_WIDTH}>
          {matches => {
            const cookiesProps = matches
              ? { scrollSettings: SCROLL_SETTINGS }
              : {};

            return <Cookies {...cookiesProps} />;
          }}
        </MediaQuery>
        <AppModal origin="search" />
      </SahContainer>
    );
  }
}

export default withClientConfig()(SearchPage);
